import React, { useState, useEffect, useCallback } from "react";
import assert from "utils/assert";
import { useToast, Flex, Box, Divider, Button, HStack, Input, VStack, Text, Mark } from "@chakra-ui/react";
import { useQuery } from "react-query";
import {
  getAIContext,
  postChat,
  apiGetThread,
  postSourcesUpdate,
  get_json_content,
  displayThreadStatus,
  Thread,
  AGENT_CANVAS,
} from "api/ai/ai.service";
import MarkdownText from "components/MarkdownText";
import { showSuccessToast } from "utils";
import LocalSpinner from "components/LocalSpinner";
import FileListWithBrowseOrUpload, { FileList } from "../FileListWithBrowseOrUpload";
import { getFolderForRound } from "api/common/fileinfo";
import { useSelector } from "react-redux";
import { AIFeedbackThumbsUpDown, AIFeedbackComment } from "./AIFeedbackControls";
import fileinfoService from "api/common/fileinfo.service";
import { SmallText } from "components/text/TextTypes";
import { multipartAnswerToString } from "pages/ISSB/IssbDataInput/helper";
import {
  ArrowCircleUp,
  Copy,
  Files,
  Sparkle,
  Chat,
  ThumbsUp,
  ThumbsDown,
  ClockCounterClockwise,
  Info,
  Eye,
  EyeClosed,
  HourglassMedium,
} from "@phosphor-icons/react";
import { colors } from "theme/foundations/colors";
import DevOnly from "../../../../context/Auth/DevOnly";
import AIChatMessage from "./AIChatMessage";
import LifecycleState from "api/common/LifecycleState";
import * as Sentry from "@sentry/react";
import Wrapper from "utils/Wrapper";
import { useAuth } from "context";
import questionnaireService from "api/issb/questionnaire.service";

/**
 * The guts of AIAssistPanel. Chat plus ESG-features:
 *
 *  - load/save thread from our backend
 *  - File upload
 *  - round -> folder -> limit sources with a FileinfoSpec
 *  - (fixed) Buttons
 *
 * the context sent to the AI is managed via setAIContext()
 *
 * @param {*} param0
 * @param {FileInfoSpec?} fileinfoSpec Normally unset, and we make this from the current round
 * @returns
 */
export default function AIChatWidget({
  reportdoc_id,
  threadId,
  setThreadId,
  cannotAddSource,
  fileinfoSpec,
  agent = "chat",
  onChatResponse,
  onThreadUpdate,
  height,
  dontShowEmptyChatDefaultMessage = false,
}) {
  // Use a wrapper to avoid a stale thread getting baked into the polling function
  const [threadWrapper, setThreadWrapper] = useState(new Wrapper(null));
  const [messages, setMessages] = useState([]);

  const [inputValue, setInputValue] = useState("");
  const [buttons, setButtons] = useState([]);
  const [isLoading, setIsLoading] = useState(); // in-chat spinner
  const [loadingThread, setLoadingThread] = useState(false); // initial thread load spinner
  const [showAllMessages, setShowAllMessages] = useState(false); // for debug: see tool calls etc
  const [slow, setSlow] = useState(false); // slow-mode for AI
  // scroll to the bottom of the messages
  const messagesEndRef = React.useRef(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Poll for updates
  // Hello @Ye-Lynn - notes on where polling is:
  // - handleSendMessage should start polling
  // - pollThread is called to start polling, and then polls repeatedly
  // - Thread.state is used to switch polling off.
  // - While polling, the thread.status is displayed so the user can see things are happening
  // TODO:
  // - TODO If handleSendMessage gets a timeout waiting on postChat
  //   - we should continue polling until the thread is done / failed.
  // - TODO pollThread can also call handleChatResponse (e.g. for the case where there was a timeout)
  //    - But be careful to avoid double-processing the same response.
  // - TODO a nice user message if there is an error (other than timeout, which we'll handle with polling)
  // - TODO if you leave a report-writer page while it's waiting for a response, then return
  //    -- use thread.state = running to restart polling when you return.
  // Stretch goal:
  // - TODO if you visit an unready report - show thread.status and updates as they happen (including reloading the reportDoc which onChatResponse does).

  // Use ref vs state to avoid stale closures and prevent unnecessary re-render
  const pollingState = React.useRef({ polling: false, timeout: null }); // polling for new messages
  const [threadStatus, setThreadStatus] = useState();
  const [latestPolledMessage, setLatestPolledMessage] = useState();

  /**
   * Handle a chat response by extracting the AI message, processing content,
   * updating messages state, and calling callbacks.
   * Can be called from handleSendMessage or pollThread.
   * Note: Why not fix on just one of handleSendMessage or pollThread?
   *  - handleSendMessage can be faster (by 1 second)
   *  - pollThread can be more reliable (e.g. if the connection is lost)
   * @param {ChatResponse|null} response - The chat response (null if error occurred)
   * @param {Error|null} error - The error if one occurred
   * @param {ChatMessage[]} messagesWithUserMessage - Messages array with user message already added
   * @param {Function} setMessages - State setter for messages
   * @param {Function} scrollToBottom - Function to scroll to bottom
   * @param {Function} onChatResponse - Optional callback for chat response
   *
   *
   * TODO: to clean up - do we need both response and thread here? consider separating the data parsing and error handling.
   */
  const handleChatResponse = useCallback(
    (
    response,
    thread,
    error,
    messagesWithUserMessage,
    setMessages,
    scrollToBottom,
    onChatResponse
  ) => {
    if (!isLoading) {
      console.error("handleChatResponse called while not loading! :(");
    } else {
      console.log("handleChatResponse called while loading :)", isLoading);
    }
    const threadState = thread?.state || "error"; // Note: no thread?.state would mean something went wrong
    setIsLoading(false);
    // Stop polling only if thread state is done or failed (not if still running)
    if (threadState === "done" || threadState === "failed") {
      pollingState.current.polling = false;
      if (pollingState.current.timeout) {
        clearTimeout(pollingState.current.timeout);
        pollingState.current.timeout = null;
      }
    }
    /** @type {ChatMessage} */
    let newAIMessage = null;
    try {
      // robust unpacking of a message from the response
      newAIMessage = thread?.messages[thread.messages.length - 1];
      if (!newAIMessage) {
        console.error("No message from AI response", response);
        newAIMessage = {};
      }
      const json_content = get_json_content(thread);
      let content = json_content?.message || json_content?.answer || newAIMessage?.content;
      if (!content) {
        if (response?.error) {
          content = response.rate_limit_error
            ? "Sorry - Service overloaded! Please try again later."
            : "Sorry - There was an error: " + response.error;
        } else if (response) {
          content = JSON.stringify(response);
        } else {
          content = "No response received from server.";
        }
      }
      if (typeof content !== "string") {
        // hack: multipart answer?
        content = multipartAnswerToString(content);
      }
      console.log("newAIMessage - change content", newAIMessage.content, content);
      newAIMessage.content = content;
    } catch (err) {
      console.error("Error processing AI response:", err);
      if (!response) {
        newAIMessage = {
          content: "Failed to get AI response.\nError: " + (error || err),
          role: "system",
          error: "" + (error || err),
        };
      } else {
        newAIMessage = { content: "Browser error processing AI response.\n" + err, role: "system", error: "" + err };
      }
      // send the error details to Sentry for potenttial debugging
      try {
        Sentry.captureException(error || err);
      } catch (sentryError) {
        console.error("(swallow) Error sending error to Sentry:", sentryError);
      }
    }
    // add the AI message / error to the messages
    console.log("newAIMessage", newAIMessage);
    const messagesWithAIMessage = [...messagesWithUserMessage, newAIMessage];
    setMessages(messagesWithAIMessage);
    scrollToBottom();
    // callback?
    if (onChatResponse) {
      onChatResponse(response);
    }
  },
    [isLoading]); // ./handleChatResponse

  /**
   * Poll the thread for updates.
   * Being called by
   *  1. handSendMessage to start polling if there is threadId estiablished.
   *  2. on page load and if the current thread is active (state is "running"). - to restore polling after a page reload.
   * @param {number} pollDelayMs - Delay in milliseconds between polls
   */
  const pollThread = useCallback(
    async (pollDelayMs) => {
      if (pollingState.current.polling && threadId) {
        const threadData = await apiGetThread(threadId);
        assert(threadData.id === threadId);
        setThreadStatus(threadData.status);
        // has it changed??
        if (!threadWrapper.value || threadWrapper.value.version !== threadData.version) {
          console.log(
            "thread changed!",
            threadData.version,
            threadWrapper.value?.version,
            threadData.status,
            threadData
          );
          threadWrapper.value = threadData;
          if (onThreadUpdate) {
            onThreadUpdate(threadData);
          }
          if (threadData.messages?.length) {
            const lastMessage = threadData.messages[threadData.messages.length - 1];
            setLatestPolledMessage(lastMessage);
          }
        }
        // done? Check state enum (not status which is display text)
        if (pollingState.current.polling && (threadData.state === "done" || threadData.state === "failed")) {
          console.log("pollThread: state done! Handling response", threadData.state);
          // Stop polling and clear timeout
          pollingState.current.polling = false;

          if (pollingState.current.timeout) {
            clearTimeout(pollingState.current.timeout);
            pollingState.current.timeout = null;
          }

          // Handle the response
          let messagesWithUserMessage = threadData?.messages?.slice(0, -1) || []; // remove last AI message since it's added in handleChatResponse
          handleChatResponse(
            null,
            threadData,
            null,
            messagesWithUserMessage,
            setMessages,
            scrollToBottom,
            onChatResponse
          );

          setIsLoading(false);
        } else if (pollingState.current.polling) {
          // keep going - clear old timeout and set new one
          if (pollingState.current.timeout) {
            clearTimeout(pollingState.current.timeout);
          }
          pollingState.current.timeout = setTimeout(() => {
            pollThread(pollDelayMs);
          }, pollDelayMs);
        }
      }
    },
    [threadId, threadWrapper, onThreadUpdate, handleChatResponse, onChatResponse]
  );

  // for restarting a thread with chat history, load the messages
  // note: not using useQuery because user input gets added instantly to messages - hence useState + useEffect
  useEffect(() => {
    if (threadId && messages.length === 0) {
      setLoadingThread(true);
      apiGetThread(threadId)
        .then((data) => {
          threadWrapper.value = data;
          setMessages(data.messages);
          // if the thread is active (state is "running") then poll for updates
          if (!data.state || data.state === "running") {
            setIsLoading(true);
            pollingState.current.polling = true;
            pollingState.current.timeout = setTimeout(() => pollThread(1000), 1000);
          } else {
            console.log("thread is not active", data.state, data.status, data);
          }
        })
        .finally(() => {
          setLoadingThread(false);
        });
    }
  }, [threadId, threadWrapper, pollThread, messages]);

  // Cleanup polling timeout on unmount
  useEffect(() => {
    const state = pollingState.current;
    return () => {
      if (state.timeout) {
        clearTimeout(state.timeout);
        state.timeout = null;
      }
      state.polling = false;
    };
  }, []);

  let aiContext = getAIContext();

  useEffect(() => {
    // TODO pick options based on context
    // Must be things the AI can answer
    let _buttons = [
      "What is ISSB?",
      "How can ESG analysis help my company?",
      // "What am I assigned to do?", // TODO
    ];
    if (aiContext?.question) {
      // TODO handle draft answers in chat as well as the typed-in answer
      if (aiContext?.answer) {
        _buttons = ["Generate a new draft answer", "Give feedback on the answer", "Check for Greenwashing"];
      } else if (!messages.length) {
        _buttons = ["Draft an answer for this question", "Check for Greenwashing"];
      } else {
        console.log("no answer? aiContext:", aiContext);
        _buttons = [];
      }
    } else if (messages?.length) {
      // avoid buttons on an open thread -- until we set useful ones
      _buttons = [];
    }
    setButtons(_buttons);
    scrollToBottom();
  }, [aiContext, messages, messages?.length]);

  // look for a Q-Round and use that folder to limit the sources
  const roundSelector = useSelector((state) => state.issbRound);
  const roundId = roundSelector?.currentRoundId;
  const folder = getFolderForRound(roundId);
  const { getAccessTokenSilently } = useAuth();
  // Note: avoid using cached round data here -- as sources may be stale
  const { data: currentRound } = useQuery(
    roundId,
    async () => {
      await getAccessTokenSilently();
      return questionnaireService.get(roundId);
    },
    {
      enabled: !!roundId,
    }
  );
  const sourceRoundIds = currentRound?.sourceRoundIds;



  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    // add the user message to the messages
    setIsLoading(true);
    const messagesWithUserMessage = [...messages, { content: inputValue, role: "user" }];
    setMessages(messagesWithUserMessage);
    setInputValue("");
    scrollToBottom();
    // call the AI...
    /** @type {ChatResponse} */
    let response = null;
    let error = null;
    let thread = null;
    try {
      /** @type {FileInfoSpec} */
      const _fileinfoSpec = Object.assign({}, fileinfoSpec);
      if (!fileinfoSpec) {
        _fileinfoSpec.prefer_fileinfo_ids = fileinfos?.map((f) => f.id);
        _fileinfoSpec.exclude_fileinfo_ids = excludedList?.map((f) => f.id);
        _fileinfoSpec.folder_id = folder;
        _fileinfoSpec.round_ids = sourceRoundIds;
      }
      // HACK: the report-writer can't see the current round - so don't send the ID
      const active_round_ids = agent === AGENT_CANVAS ? null : [roundId];

      // Call the AI!
      setThreadStatus("Calling the AI service");

      // there is threadId? just post, abandon (async) and start polling.
      if (threadId) {
        console.log("handleSendMessage: have threadId - postChat and start polling");
        postChat({
          threadId,
          message: inputValue,
          fileinfoSpec: _fileinfoSpec,
          agent,
          speed: slow ? "LOW" : "MEDIUM",
          active_round_ids,
          reportdoc_id,
          saveEdits: !!reportdoc_id,
        });
        pollingState.current.polling = true;
        pollingState.current.timeout = setTimeout(() => pollThread(1000), 1000);
      } else {
        // no threadId yet - need to wait for postChat to return threadId. Usually at the start of the conversation.
        console.log("handleSendMessage: no threadId - postChat and wait for response");
        response = await postChat({
          threadId,
          message: inputValue,
          fileinfoSpec: _fileinfoSpec,
          agent,
          speed: slow ? "LOW" : "MEDIUM",
          active_round_ids,
          reportdoc_id,
          saveEdits: !!reportdoc_id,
        });

        thread = response.thread;
        const newThreadId = response.thread?.id;
        if (!threadId && newThreadId) {
          setThreadId(newThreadId);
          // Start polling if we got a new threadId and thread state is "running"
          if (thread?.state === "running") {
            pollingState.current.polling = true;
            if (!pollingState.current.timeout) {
              pollingState.current.timeout = setTimeout(() => pollThread(1000), 1000);
            }
          }
        }
        handleChatResponse(
          response,
          thread,
          error,
          messagesWithUserMessage,
          setMessages,
          scrollToBottom,
          onChatResponse
        );
      }
    } catch (err) {
      error = err;
      console.error("Error fetching AI response:", error);
      handleChatResponse(response, thread, error, messagesWithUserMessage, setMessages, scrollToBottom, onChatResponse);
    }
  };

  const [fileinfos, setFileinfos] = useState([]);
  useEffect(() => {
    if (aiContext?.files) {
      // ids or objects?
      if (aiContext.files.length > 0 && aiContext.files[0].id) {
        // objects :)
        setFileinfos(aiContext.files);
      } else {
        console.log("ignore-for-chat: aiContext.files", aiContext.files);
      }
    }
  }, [aiContext]);

  const [excludedList, setExcludedList] = useState([]);
  const handleFileInfoListChange = (fileInfoList) => {
    console.log("handleFileInfoListChange", fileInfoList);
    // Send for AI analysis? But only once
    for (let fileInfo of fileInfoList) {
      if (fileInfo.aiDate || fileInfo._aiAnalysisRequested) continue;
      console.log("send to AI", fileInfo);
      postSourcesUpdate([fileInfo.id], true).then((data) =>
        console.log("ai analysis result", data, "for file", fileInfo)
      );
      fileInfo._aiAnalysisRequested = true; // avoid multiple requests
    }
    aiContext.files = fileInfoList;
    setFileinfos(fileInfoList);
  };
  const handleExcludedListChange = (excludedList) => {
    aiContext.excludedFiles = excludedList;
    setExcludedList(excludedList);
  };

  // don't show tool results
  let displayMessages = messages;
  if (!showAllMessages) {
    displayMessages = displayMessages.filter(
      (message) =>
        message.role !== "tool" && message.role !== "tool_result" && message.role !== "critic" && message.content
    );
    // or tool results
    displayMessages = displayMessages.filter((message) => message.json_content?.type !== "function");
    // filter out archived messages
    displayMessages = displayMessages.filter((message) => message.state !== LifecycleState.ARCHIVED);
  }

  if (loadingThread) {
    return (
      <Box>
        <LocalSpinner mt={4} w="100px" h="100px" />
        <Text>Loading thread...</Text>
      </Box>
    );
  }
  return (
    <Box height={height || "100%"} display="flex" flexDirection="column">
      <Flex flexGrow={1} overflowY="auto" flexDirection="column" pr={4}>
        {displayMessages.length > 0 &&
          displayMessages.map((message, index) => (
            <AIChatMessage
              key={index}
              threadId={threadId}
              message={message}
              setMessages={setMessages}
              allMessages={messages}
              isLastMessage={index === messages.length - 1}
              debug={showAllMessages}
            />
          ))}
        {!displayMessages.length && dontShowEmptyChatDefaultMessage === false && (
          <NoMessages setMessage={setInputValue} buttons={buttons} />
        )}
        {isLoading && (
          /* Note: this Box wrapper is needed for correct scrolling behaviour */ <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
          >
            <LocalSpinner mt={4} w="100px" h="100px" />
            <Box style={{ minHeight: "40px", marginLeft: "5px" }}>
              <Text>I'm thinking</Text>
              <Thinking threadStatus={threadStatus} latestPolledMessage={latestPolledMessage} />
            </Box>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Flex>

      {/* chat input */}
      <Box>
        <Divider mt={4} mb={2} />
        {!isLoading && messages.length > 0 && (
          <div>
            {buttons.map((question) => (
              <Button
                key={question}
                display={"inline-block"}
                m={2}
                onClick={(e) => setInputValue(question)}
                variant="secondary"
                size="xs"
              >
                {question}
              </Button>
            ))}
          </div>
        )}
        <HStack>
          <Input
            className="chat-text-input"
            bg="pureWhite"
            flexGrow={1}
            placeholder="Type a message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            fontSize="body.xs"
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                handleSendMessage();
              }
            }}
          />
          <DevOnly>
            <Button
              title="Toggle slow AI mode"
              size="sm"
              variant={slow ? "primary" : "secondary"}
              onClick={(e) => setSlow(!slow)}
              p={2}
            >
              <HourglassMedium />
            </Button>
          </DevOnly>
          <Button variant="plain" onClick={handleSendMessage} p={2} className="chat-text-send-button">
            <ArrowCircleUp size={32} weight="fill" color="#CBD5E07" />
          </Button>
          <DevOnly>
            <Button size="sm" variant="plain" onClick={(e) => setShowAllMessages(!showAllMessages)} p={2}>
              {showAllMessages ? <Eye /> : <EyeClosed />}
            </Button>
          </DevOnly>
        </HStack>
        {!cannotAddSource && (
          <FileListWithBrowseOrUpload
            onFileInfoListChange={handleFileInfoListChange}
            onExcludedListChange={handleExcludedListChange}
            fileInfoList={fileinfos}
            excludedList={excludedList}
            variant="darkTeal"
            leftIcon={<Files size={18} color={colors.pureWhite} />}
            p={0}
            canExclude={true}
            w="" // switch off w=100%
          >
            <Text fontSize="body.xs" color="pureWhite">
              Sources
            </Text>
          </FileListWithBrowseOrUpload>
        )}
      </Box>
    </Box>
  );
}

/**
 * Dev-only: This is NOT ready for users yet!
 * Note: this isn't true streaming - it doesnt get answer-in-progress tokens from the AI.
 * What it gets is updates as the agent loop moves through the steps.
 * Show interim feedback from the AI
 */
function Thinking({ threadStatus, latestPolledMessage }) {
  let message = displayThreadStatus(threadStatus);
  const messageType = latestPolledMessage?.json_content?.type;
  if (messageType === "function") {
    message = "Tool call: " + latestPolledMessage.json_content.function?.name;
  }
  return <SmallText>{message}</SmallText>;
}

function NoMessages({ setMessage, buttons }) {
  return (
    <VStack>
      <Sparkle size={64} weight="fill" color={colors.headingLime} />
      <Text textAlign="center" fontSize="heading.m" color="teal" variant="subtitle">
        Guiding you every step of the way for seamless ESG reporting.
      </Text>
      <div>
        <center>
          {buttons.map((question) => (
            <Button
              fontSize="body.xs"
              varient="secondary"
              display={"inline-block"}
              m={2}
              onClick={(e) => setMessage(question)}
              variant="secondary"
              key={question}
            >
              {question}
            </Button>
          ))}
        </center>
      </div>
    </VStack>
  );
}
